import { apm } from '@elastic/apm-rum';
import { ConfigProvider, notification, Typography } from 'antd';
import { ConfigProvider as CountryPhoneNumberInputConfigProvider } from 'antd-country-phone-input';
import ptBr from 'antd/lib/locale/pt_BR';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import moment from 'moment';
import 'moment/locale/pt-br';
import React, { useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import br from 'world_countries_lists/data/countries/en/world.json';

import useAPIClient, { APIClient } from '../api/useAPIClient';
import { errorResultDefaults, ErrorResultStatusType } from '../components/ErrorResult';
import useTypedParams from '../hooks/useTypedParams';

const throwApiNotificationError = (
  requestError: AxiosError,
  title: React.ReactNode,
  subTitle: React.ReactNode
) => {
  notification.error({
    duration: 0,
    // TODO: TRADUZIR ISSO AQ NO FINAL
    message: title || 'Erro',
    description: (
      <>
        {subTitle}
        {requestError.response?.headers['x-elasticapm-traceid'] && (
          <>
            <br />
            <br />
            <Typography.Text code>
              Trace ID: {requestError.response?.headers['x-elasticapm-traceid']}
            </Typography.Text>
          </>
        )}
      </>
    ),
  });
};

const createQueryClient = (tenant: string, api: APIClient) => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: async ({ queryKey, signal }) => {
          const [route, requestConfig] = queryKey as [string, AxiosRequestConfig];

          const { data } = await api.request({
            url: `${route}`,
            method: 'GET',
            signal,
            ...requestConfig,
          });

          return data;
        },
        onError: (err) => {
          if (!axios.isAxiosError(err) || (err.response && err.response.status >= 500)) {
            apm.captureError(err as Error);
          }

          if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
            const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || window.location.origin;
            window.location.href = `${LOGIN_URL}/${tenant}/accounts/login/?next=${window.location.href}`;
          }
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        onError: (error) => {
          apm.captureError(error as Error);

          const status = (error as AxiosError).response?.status.toString() as ErrorResultStatusType;
          const { title, subTitle } = errorResultDefaults[status || 'error'] || {};
          throwApiNotificationError(error as AxiosError, title, subTitle);
        },
      },
    },
  });
};

moment.locale('pt-br');

export default function AppProvider({ children }: React.PropsWithChildren<object>) {
  const { tenant } = useTypedParams({ tenant: String });
  const client = useAPIClient();

  const queryClient = useMemo(() => createQueryClient(tenant, client), [client, tenant]);

  return (
    <ConfigProvider locale={ptBr}>
      <QueryClientProvider client={queryClient}>
        <CountryPhoneNumberInputConfigProvider locale={br}>
          <HelmetProvider>{children}</HelmetProvider>
        </CountryPhoneNumberInputConfigProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ConfigProvider>
  );
}
